import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { InfoPuntajes } from '../../types/team';

import RankingMobile from './Mobile';
import { ContentModalWinner, ModalChart } from './winners';

import {
  Button,
  Container,
  Divider,
  Dropdown,
  Modal,
  NavBar,
  Tabs,
} from '@/components';
import { AdSlot } from '@/components/GoogleAds/AdSlot';
import useAuth from '@/hooks/useAuth';
import { useGoogleAds } from '@/hooks/useGoogleAds';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMetaData } from '@/hooks/useMetaData';
import { usePermutive } from '@/hooks/usePermutive';
import ROUTES from '@/router/routes';
import {
  getRanking,
  getRankingLocalidad,
  getRankingPronvicias,
  initProvinciaRanking,
  initRankings,
} from '@/services/ranking';
import { getTshirtAndShieldByIds } from '@/services/utils';
import { RankingTO } from '@/types/ranking';
import { formatNumberToLocale, letterGenderById } from '@/utils';
import { ADS_DIV_ID, SECTION_NAME_FOR_ADS } from '@/utils/ads';

export const formatDni = (dni: string) => {
  if (!dni) return '';

  const rx = /^(\w{2})(\w{3})(\w{3})$/;
  if (dni.split(' ').length === 1) {
    return `DNI ${dni.replace(rx, '$1.$2.$3')}`;
  }
  // Split document into type and number parts
  const [docType, numberPart] = dni.includes(' ')
    ? dni.split(' ', 2)
    : [dni.substring(0, 5), dni.substring(5).trim()];

  if (!numberPart) return dni;

  // If the number part consists of exactly 3 digits
  return `${docType} ${numberPart.replace(
    /^(\w{2})(\w{3})(\w{3})$/,
    '$1.$2.$3'
  )}`;
};

const DTitem = ({
  DT,
  index,
  setSelectedWinner,
  onOpenChart,
  onOpenWinnerTeam,
  setIsSelectedWinnerF5,
  dropOptionSelected,
  isGeneralTournament,
}: {
  DT: RankingTO;
  index: number;
  setSelectedWinner?: (DT: RankingTO) => void;
  onOpenChart: () => void;
  onOpenWinnerTeam: () => void;
  setIsSelectedWinnerF5?: () => void;
  dropOptionSelected: string;
  isGeneralTournament?: boolean;
}) => {
  const { userInitialData } = useAuth();
  useMetaData({
    title: 'Rankings',
  });

  const showAsWinner = index === 0 && dropOptionSelected === 'Ranking General';

  const handleShowModalWinner = async () => {
    const response = await getTshirtAndShieldByIds([parseInt(DT.idUsuarioDT)]);

    if (response.estado && response.camisetas) {
      setSelectedWinner?.({ ...DT, camisetaSvg: response.camisetas[0].cami });
      onOpenWinnerTeam();
      setIsSelectedWinnerF5?.();
    }
  };

  const canViewTeam =
    showAsWinner &&
    (!isGeneralTournament ||
      (isGeneralTournament && userInitialData?.todoPublicado));

  return (
    <>
      <div className="grid grid-cols-12 px-10 mt-6">
        <div className="col-span-2">
          <div
            className={`p-3 w-10 h-10 rounded-full flex justify-center items-center ${
              showAsWinner ? 'bg-gdt-blue-header' : 'bg-gdt-blue-light2'
            }`}
          >
            <span
              className={`font-bold ${
                showAsWinner ? 'text-white' : 'text-gdt-blue'
              }`}
            >
              {index + 1}
            </span>
          </div>
        </div>
        <div className="flex flex-col col-span-7">
          <span className="font-bold">{DT.dt}</span>
          {canViewTeam ? (
            <div className="">
              <Button
                className="font-bold text-sm text-end relative group"
                href="#"
                id="my-anchor-element"
                type="link"
                variant="text"
                onClick={handleShowModalWinner}
              >
                <span>{DT.equipo}</span>
                <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 w-64 bg-gdt-gray-2 rounded-lg text-white text-center p-2 z-10 invisible group-hover:visible">
                  Al hacer clic acá vas a poder ver información del equipo
                </div>
              </Button>
            </div>
          ) : (
            <span className="font-semibold text-gdt-gray-4">
              {DT.equipo ? DT.equipo : '\u00A0'}
            </span>
          )}

          <span className="font-semibold text-gdt-gray-4">
            {DT.provincia ? DT.provincia : '\u00A0'}
          </span>
        </div>
        <div className="flex flex-col col-span-3 text-end">
          <span className="col-span-2 font-bold text-end">{DT.puntos}</span>
          {showAsWinner &&
            DT.idUsuarioDT !==
              (userInitialData?.idUsuarioDT?.toString() ?? '') && (
              <div className="relative group">
                <Button
                  className="font-bold text-sm text-end"
                  href="#"
                  id="my-anchor-element"
                  type="link"
                  variant="text"
                  onClick={() => {
                    setSelectedWinner?.(DT);
                    onOpenChart();
                  }}
                >
                  Comparar
                </Button>
              </div>
            )}
          {/* 
          <Tooltip
            anchorSelect="#my-anchor-element"
            content="Al hacer clic acá vas a poder ver información del equipo"
          /> */}
        </div>
        {showAsWinner && (
          <>
            <div className="col-span-2" />
            <Divider className="my-3 col-span-10" type="horizontal" />
            <div className="col-span-2" />
            <div className="flex justify-between col-span-10 text-end">
              <div className="flex flex-col">
                <span className=" text-start">
                  {DT?.edad ? `${DT.edad} años` : ''}{' '}
                </span>
                <span>{formatDni(DT.dni)}</span>
              </div>
              <div className="flex flex-col">
                <span>
                  Ranking general: <strong>{DT.puestoGral}</strong>
                </span>
                <span>
                  Hincha de: <strong>{DT.hinchaDe}</strong>
                </span>
              </div>
            </div>
          </>
        )}
      </div>
      <Divider className="my-3" type="horizontal" />
    </>
  );
};

const RankingPage = () => {
  const navigate = useNavigate();
  const goToCompleteData = () => {
    navigate(ROUTES.editAccount.myData);
  };
  useMetaData({
    title: 'Rankings',
  });
  const { isDesktop } = useIsDesktop();
  const [selectedWinner, setSelectedWinner] = useState<RankingTO | null>(null);
  const [isSelectedWinnerF5, setIsSelectedWinnerF5] = useState(false);
  const [isOpenChartModal, setIsOpenChartModal] = useState<0 | 1 | 2>(0);
  const [isOpenWinnerTeamModal, setIsOpenWinnerTeamModal] = useState(false);
  const [isNoRankingsModalOpen, setIsNoRankingsModalOpen] = useState(false);
  const { userInitialData } = useAuth();
  const [selectedTab, setSelectedTab] = useState({
    f11: 0,
    f5: 0,
  });
  const [dropOptioSelected, setDropOptionSelected] = useState(-1);
  const [isLoadingF11, setIsLoadingF11] = useState(false);
  const [isLoadingF5, setIsLoadingF5] = useState(false);
  const [data, setData] = useState<Record<'f5' | 'f11', RankingTO[]>>({
    f11: [],
    f5: [],
  });
  const [msjFechaFinal, setMsjFechaFinal] = useState<{ [key: string]: string }>(
    {
      f11: '',
      f5: '',
    }
  );
  const [rawData, setRawData] = useState<Record<'f5' | 'f11', RankingTO[]>>({
    f11: [],
    f5: [],
  });
  const [infoPuntajesF5, setInfoPuntajesF5] = useState<
    InfoPuntajes | undefined
  >(undefined);
  const [infoPuntajesF11, setInfoPuntajesF11] = useState<
    InfoPuntajes | undefined
  >(undefined);

  useGoogleAds({ adsSections: SECTION_NAME_FOR_ADS.rankings });

  const { trackPageView, isReady: isPermutiveReady } = usePermutive();

  useEffect(() => {
    // Permutive esta cargado
    if (isPermutiveReady) {
      trackPageView({
        section: SECTION_NAME_FOR_ADS.rankings,
        kw_premium: userInitialData?.premium,
        kw_edad: userInitialData?.edad_dt,
        kw_sexo: letterGenderById(userInitialData?.idSexo),
        kw_equipo: userInitialData?.club_hincha,
        kw_fecha: userInitialData?.fechaActiva.orden,
        kw_pines: userInitialData?.pinesDisponibles,
        kw_saldo: userInitialData?.saldo,
        kw_puntosporfecha: userInitialData?.puntos_fecha,
        kw_puntosportorneo: userInitialData?.puntos_general,
        kw_posicionporfecha: userInitialData?.posicion_fecha,
        kw_recibirinfo: userInitialData?.recibeInformacion,
        kw_posicionportorneo: userInitialData?.posicion_general,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermutiveReady]);

  useEffect(() => {
    if (!isDesktop) {
      return;
    }

    const fetchData = async () => {
      setData((prev) => ({
        ...prev,
        f11: [],
      }));
      // General ranking (option 0)
      if (dropOptioSelected === 0) {
        if (userInitialData?.fechaMostrarRankingF11?.id === undefined) {
          setData((prev) => ({
            ...prev,
            f11: [],
          }));
          return;
        }
        const rankingF11 = await getRanking(
          selectedTab.f11 ? 'f11' : 'f11Fecha',
          userInitialData?.fechaMostrarRankingF11?.id.toString() ?? '1111'
        ).catch(() => {
          setData((prev) => ({
            ...prev,
            f11: [],
          }));
          return null;
        });

        if (!rankingF11) {
          setData((prev) => ({
            ...prev,
            f11: [],
          }));
          return;
        }
        if (rankingF11 && 'top_fecha' in rankingF11) {
          setData((prev) => ({
            ...prev,
            f11: rankingF11.top_fecha as RankingTO[],
          }));
          setRawData((prev) => ({
            ...prev,
            f11: rankingF11.top_fecha as RankingTO[],
          }));
        } else {
          setData((prev) => ({
            ...prev,
            f11: rankingF11.top_general as RankingTO[],
          }));
        }
        if (rankingF11.msjFechaFinal) {
          setMsjFechaFinal((prev) => ({
            ...prev,
            f11: rankingF11.msjFechaFinal ?? '',
          }));
        }
      }
      if (dropOptioSelected === 1) {
        const pronviciaData = await initProvinciaRanking();
        if (!pronviciaData?.datos?.idProvincia || !userInitialData?.provincia) {
          setData((prev) => ({
            ...prev,
            f11: [],
          }));
          return;
        }
        const provinciasRanking = await getRankingPronvicias(
          pronviciaData.datos.idProvincia.toString(),
          selectedTab.f11 === 0
        );
        setData((prev) => ({
          ...prev,
          f11: provinciasRanking.datos.datos.map((e) => ({
            objectId: Math.random().toString(36),
            dt: e.dt,
            equipo: e.equipo,
            puntos: formatNumberToLocale(e.puntos),
            dni: e.idDt
              .toString()
              .replace(/(\d{2})(\d{3})(\d{3})/, 'XX.XXX.$3'),
            idUsuarioDT: e.idDt.toString(),
            hinchaDe: '',
            puestoGral: e.posicion.toString(),
            provincia: pronviciaData.datos.nombreProvincia,
            edad: '',
            escudo: '',
            localidad: '',
          })),
        }));
      }

      if (dropOptioSelected === 2) {
        if (!userInitialData?.barrio) {
          setData((prev) => ({
            ...prev,
            f11: [],
          }));
          return;
        }

        const localidadesRanking = await getRankingLocalidad(
          !userInitialData?.dtExtranjero
        );
        if (selectedTab.f11) {
          setData((prev) => ({
            ...prev,
            f11: localidadesRanking.datos.datosGeneral.map((e) => ({
              objectId: Math.random().toString(36),
              dt: e.dt,
              equipo: e.equipo,
              puntos: formatNumberToLocale(e.puntos),
              dni: '',
              idUsuarioDT: e.idUsuarioDT.toString(),
              hinchaDe: '',
              puestoGral: e.posicion.toString(),
              provincia: '',
              edad: '',
              escudo: '',
              localidad: '',
            })),
          }));
        } else {
          setData((prev) => ({
            ...prev,
            f11: localidadesRanking.datos.datosFecha.map((e) => ({
              objectId: Math.random().toString(36),
              dt: e.dt,
              equipo: e.equipo,
              puntos: e.puntos.toString(),
              dni: '',
              idUsuarioDT: e.idUsuarioDT.toString(),
              hinchaDe: '',
              puestoGral: e.posicion.toString(),
              provincia: '',
              edad: '',
              escudo: '',
              localidad: '',
            })),
          }));
        }
      }
    };

    setIsLoadingF11(true);
    fetchData().finally(() => setIsLoadingF11(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTab.f11,
    userInitialData?.fechaMostrarRankingF11?.id,
    dropOptioSelected,
    isDesktop,
    userInitialData?.dtExtranjero,
    userInitialData?.barrio,
    userInitialData?.provincia,
  ]);

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      f5: [],
    }));
    if (!isDesktop || !userInitialData?.fechaMostrarRankingF5?.id) {
      return;
    }

    const fetchData = async () => {
      const ranking = await getRanking(
        selectedTab.f5 ? 'f5' : 'f5Fecha',
        userInitialData?.fechaMostrarRankingF5?.id.toString()
      ).catch(() => {
        setData((prev) => ({
          ...prev,
          f5: [],
        }));
        return null;
      });

      if (!ranking) {
        setData((prev) => ({
          ...prev,
          f5: [],
        }));
        return;
      }

      if (ranking.top_fecha) {
        setData((prev) => ({
          ...prev,
          f5: ranking?.top_fecha ?? ranking?.top_general ?? [],
        }));
        setRawData((prev) => ({
          ...prev,
          f5: ranking?.top_fecha ?? ranking?.top_general ?? [],
        }));
      } else {
        setData((prev) => ({
          ...prev,
          f5: ranking.top_general as RankingTO[],
        }));
      }
      if (ranking?.msjFechaFinal) {
        setMsjFechaFinal((prev) => ({
          ...prev,
          f5: ranking.msjFechaFinal ?? '',
        }));
      }
    };

    setIsLoadingF5(true);
    fetchData().finally(() => setIsLoadingF5(false));
  }, [
    isDesktop,
    selectedTab.f5,
    userInitialData?.fechaMostrarRankingF5?.id,
    dropOptioSelected,
  ]);

  /*
  useEffect(() => {
    const fetchData = async () => {
      if (userInitialData?.tiene_equipo_f5) {
        const f5Data = await getCurrentTeamF5();
        if (f5Data.estado) {
          setInfoPuntajesF5(f5Data?.infoPuntajes);
        }
      }
    };
    fetchData();
  }, [userInitialData?.tiene_equipo_f5]);
  */
  useEffect(() => {
    const fetchData = async () => {
      const responseInitRanking = await initRankings();
      if (!responseInitRanking?.estado) {
        return;
      }
      if (userInitialData?.tiene_equipo_f5) {
        setInfoPuntajesF5(responseInitRanking?.rnkF5);
      }
      setInfoPuntajesF11(responseInitRanking?.rnkF11);
    };
    fetchData();
  }, [userInitialData?.tiene_equipo_f5]);

  useEffect(() => {
    setDropOptionSelected(0);
  }, []);

  useEffect(() => {
    const shouldShowModal =
      isDesktop &&
      dropOptioSelected !== -1 &&
      data?.f11?.length === 0 &&
      data?.f5?.length === 0 &&
      userInitialData?.fecha_publicada === false;

    setIsNoRankingsModalOpen(shouldShowModal);
  }, [
    data.f11,
    data.f5,
    isDesktop,
    dropOptioSelected,
    userInitialData?.fecha_publicada,
  ]);

  const BannersText = {
    f11: [
      {
        // fecha: 'el mejor de la fecha gana',
        // premio: '$1.500.000',
        // premium: 'si es premium',
        // noPremium: 'y $100.000 si no es premium',
        src: '/assets/images/imgs-ranking-primera-fecha.jpg',
      },
      {
        // fecha: 'EL CAMPEÓN DE GRAN DT GANA',
        // premium: 'si es premium',
        // premio: '$8.500.000',
        // noPremium: 'y $500.000 si no es premium',
        src: '/assets/images/imgs-ranking-primera-torneo.jpg',
      },
    ],

    f5: [
      {
        // fecha: 'el mejor de la fecha gana',
        // premio: '$650.000',
        src: '/assets/images/imgs-ranking-futbol5-fecha.jpg',
      },
      {
        // fecha: 'EL CAMPEÓN DE FÚTBOL 5 SE LLEVA',
        // premio: '$3.000.000',
        src: '/assets/images/imgs-ranking-futbol5-torneo.jpg',
      },
    ],
  };

  const f11DropOptions = [
    { label: 'Ranking General', value: 0, disabled: false },
  ];

  if (userInitialData?.dtExtranjero) {
    f11DropOptions.push({
      label: 'Ranking de tu país',
      value: 2,
      disabled: false,
    });
  } else {
    f11DropOptions.push(
      {
        label: 'Ranking de tu provincia',
        value: 1,
        disabled: false,
      },
      {
        label: 'Ranking de tu localidad/barrio',
        value: 2,
        disabled: false,
      }
    );
  }

  const noRankingText = 'No existen rankings disponibles en este momento.';

  const f5DropOptions = [
    { label: 'Ranking General', value: 0, disabled: false },
  ];
  const noDataTextF11 = useMemo(() => {
    if (isLoadingF11 || data?.f11?.length > 0) return '';
    if (selectedTab.f11 === 1 && msjFechaFinal?.f11?.length > 0)
      return msjFechaFinal.f11;
    return noRankingText;
  }, [isLoadingF11, selectedTab.f11, data?.f11, msjFechaFinal.f11]);

  const noDataTextF5 = useMemo(() => {
    if (isLoadingF5 || data?.f5?.length > 0) return '';
    if (selectedTab.f5 === 1 && msjFechaFinal?.f5?.length > 0)
      return msjFechaFinal.f5;
    return noRankingText;
  }, [isLoadingF5, selectedTab.f5, data?.f5, msjFechaFinal.f5]);

  if (!isDesktop) {
    return <RankingMobile />;
  }

  return (
    <NavBar
      desktop
      mobile={{
        leftContent: {
          showMenuAction: true,
          items: true,
        },
        centerContent: {
          displayLogo: true,
        },
      }}
    >
      <Container variant="default">
        {/* banner horizontal */}
        <AdSlot
          className="mt-4"
          divId={ADS_DIV_ID.horizontal}
          variant="HORIZONTAL"
        />
        <div className="mx-auto">
          <h2 className="text-2xl mb-3 mt-5">Rankings</h2>
          <p className="text-sm font-semibold text-gdt-gray-2">
            Conocé a los ganadores de la fecha y del torneo. <br />
          </p>

          {!userInitialData?.barrio && !userInitialData?.dtExtranjero && (
            <p className="text-sm font-semibold text-gdt-gray-2">
              Para conocer el ranking de tu provincia o localidad, debés
              completar tus datos.{' '}
              <button
                className="text-gdt-blue underline"
                onClick={goToCompleteData}
              >
                Ir a completar
              </button>
            </p>
          )}

          <div className="grid grid-cols-2 gap-x-3 mt-10 w-full">
            <div className="flex flex-col gap-y-6 col-span-1 ">
              <h2 className="text-2xl">Primera División</h2>
              <div className="flex flex-col rounded-lg bg-white pb-10">
                {/* <div
                  className={`relative h-44 bg-cover bg-center rounded-t-lg flex flex-col items-center justify-center`}
                  style={{
                    backgroundImage: `url('${
                      BannersText.f11[selectedTab.f11].src
                    }')`,
                  }}
                > */}
                {/* <p className="uppercase text-white font-normal  text-3xl z-10 font-bebas md:px-2 lg:px-0 text-center leading-7">
                    {BannersText.f11[selectedTab.f11].fecha}{' '}
                    <span className="text-4xl">
                      {BannersText.f11[selectedTab.f11].premio}
                    </span>{' '}
                    {BannersText.f11[selectedTab.f11].premium}{' '}
                  </p>
                  <p className="uppercase text-white text-2xl font-bebas font-normal">
                    {BannersText.f11[selectedTab.f11].noPremium}
                  </p> */}
                {/* </div> */}
                <figure className="rounded-t-lg overflow-hidden">
                  <img src={BannersText.f11[selectedTab.f11].src} />
                </figure>
                <div className="mx-auto bg-gdt-gray-7 mt-7 p-2 rounded-lg flex gap-x-2 items-center ">
                  <span className="text-center font-bold text-gdt-gray-2">
                    MI POSICIÓN EN LA FECHA
                  </span>
                  <span className="font-bold text-lg text-gdt-blue">
                    {infoPuntajesF11?.posicionFecha && rawData?.f11.length > 0
                      ? formatNumberToLocale(
                          infoPuntajesF11?.posicionFecha || 0
                        )
                      : '-'}
                  </span>
                </div>
                <div className="border-b border-gdt-gray-4">
                  <Tabs
                    activeTab={selectedTab.f11}
                    className="mt-4"
                    tabs={[
                      {
                        name: `${userInitialData?.fechaMostrarRankingF11?.nombre}`,
                      },
                      { name: 'Torneo General' },
                    ]}
                    variant="underline"
                    onClick={(e) => setSelectedTab({ ...selectedTab, f11: e })}
                  />
                </div>
                <div className="w-80 mx-auto mt-6">
                  <Dropdown
                    className=""
                    options={f11DropOptions}
                    selected={
                      f11DropOptions.find(
                        (e) => e.value === dropOptioSelected
                      ) || f11DropOptions[0]
                    }
                    onChange={(e) => setDropOptionSelected(e.value)}
                  />
                </div>
                <div className="grid grid-cols-12 px-10 mt-6">
                  <span className="col-span-2 font-semibold text-gdt-gray-2">
                    Posición
                  </span>
                  <span className="col-span-8 font-semibold text-gdt-gray-2">
                    DT
                  </span>
                  <span className="col-span-2 font-semibold text-gdt-gray-2 text-end">
                    Puntos
                  </span>
                </div>
                {data?.f11?.slice(0, 10).map((DT, index) => (
                  <DTitem
                    key={DT.objectId}
                    DT={DT}
                    dropOptionSelected={
                      f11DropOptions.find((e) => e.value === dropOptioSelected)
                        ?.label || 'Ranking General'
                    }
                    index={index}
                    isGeneralTournament={selectedTab.f11 === 1}
                    setIsSelectedWinnerF5={() => setIsSelectedWinnerF5(false)}
                    setSelectedWinner={setSelectedWinner}
                    onOpenChart={() => setIsOpenChartModal(1)}
                    onOpenWinnerTeam={() => setIsOpenWinnerTeamModal(true)}
                  />
                ))}
                <div className="text-center text-gdt-gray-2 mt-4">
                  {noDataTextF11}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-y-6 col-span-1">
              <h2 className="text-2xl">Fútbol 5</h2>
              <div className="flex flex-col rounded-lg bg-white pb-10">
                {/* <div
                  className="relative bg-no-repeat bg-cover h-44 rounded-t-lg"
                  style={{
                    backgroundImage: `url('${
                      BannersText.f5[selectedTab.f5].src
                    }')`,
                  }}
                > */}
                {/* <div className="uppercase text-white font-normal font-bebas text-3xl z-10  gap-2 items-center flex justify-center h-full">
                    <span>{BannersText.f5[selectedTab.f5].fecha}</span>{' '}
                    <span className="text-4xl font-normal ">
                      {BannersText.f5[selectedTab.f5].premio}
                    </span>
                  </div> */}
                {/* </div> */}
                <figure className="rounded-t-lg overflow-hidden">
                  <img src={BannersText.f5[selectedTab.f5].src} />
                </figure>
                <div className="mx-auto bg-gdt-gray-7 mt-7 p-2 rounded-lg flex gap-x-2 items-center ">
                  <span className="text-center font-bold text-gdt-gray-2">
                    MI POSICIÓN EN LA FECHA
                  </span>
                  <span className="font-bold text-lg text-gdt-blue">
                    {infoPuntajesF5?.posicionFecha && rawData?.f5.length > 0
                      ? formatNumberToLocale(infoPuntajesF5?.posicionFecha ?? 0)
                      : '-'}
                  </span>
                </div>
                <div className="border-b border-gdt-gray-4">
                  <Tabs
                    activeTab={selectedTab.f5}
                    className="mt-4"
                    tabs={[
                      {
                        name: `${userInitialData?.fechaMostrarRankingF5?.nombre}`,
                      },
                      { name: 'Torneo General' },
                    ]}
                    variant="underline"
                    onClick={(e) => setSelectedTab({ ...selectedTab, f5: e })}
                  />
                </div>
                <div className="w-80 mx-auto mt-6">
                  <Dropdown
                    className=""
                    options={f5DropOptions}
                    selected={{ label: 'Ranking General', value: 1 }}
                    onChange={() => null}
                  />
                </div>
                <div className="grid grid-cols-12 px-10 mt-6">
                  <span className="col-span-2 font-semibold text-gdt-gray-2">
                    Posición
                  </span>
                  <span className="col-span-8 font-semibold text-gdt-gray-2">
                    DT
                  </span>
                  <span className="col-span-2 font-semibold text-gdt-gray-2 text-end">
                    Puntos
                  </span>
                </div>
                {data.f5.slice(0, 10).map((DT, index) => (
                  <DTitem
                    key={DT.objectId}
                    DT={DT}
                    dropOptionSelected={
                      f5DropOptions.find((e) => e.value === dropOptioSelected)
                        ?.label || 'Ranking General'
                    }
                    index={index}
                    isGeneralTournament={selectedTab.f5 === 1}
                    setIsSelectedWinnerF5={() => setIsSelectedWinnerF5(true)}
                    setSelectedWinner={setSelectedWinner}
                    onOpenChart={() => setIsOpenChartModal(2)}
                    onOpenWinnerTeam={() => setIsOpenWinnerTeamModal(true)}
                  />
                ))}
                <div className="text-center text-gdt-gray-2 mt-4">
                  {noDataTextF5}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* box banners */}
        <div className="justify-center flex lg:space-x-[99px] lg:mb-12 lg:mt-14 my-6">
          <AdSlot divId={ADS_DIV_ID.caja1} variant="BOX" />
          <AdSlot divId={ADS_DIV_ID.caja2} variant="BOX" />
          <AdSlot divId={ADS_DIV_ID.caja3} variant="BOX" />
        </div>
        <Modal
          className="pt-7 px-10 bg-gdt-gray-7"
          controlledOnClose={() => {
            setIsOpenWinnerTeamModal(false);
            setSelectedWinner(null);
          }}
          isControlledOpen={isOpenWinnerTeamModal}
          render={() => {
            return (
              <ContentModalWinner
                DT={selectedWinner}
                isDate={
                  isSelectedWinnerF5
                    ? selectedTab.f5 === 0
                    : selectedTab.f11 === 0
                }
                isF5={isSelectedWinnerF5}
              />
            );
          }}
          size="xs"
          title={
            isSelectedWinnerF5
              ? selectedTab.f5 === 0
                ? 'Ganador de la fecha'
                : 'Ganador del torneo'
              : selectedTab.f11 === 0
              ? 'Ganador de la fecha'
              : 'Ganador del torneo'
          }
          titleClassName="text-xl"
          variant="controlled"
        />{' '}
        <Modal
          className="pt-7 px-10 bg-gdt-gray-7"
          controlledOnClose={() => {
            setIsOpenChartModal(0);
            setSelectedWinner(null);
          }}
          isControlledOpen={isOpenChartModal !== 0}
          render={() => {
            return (
              <ModalChart
                isF5={isOpenChartModal === 2}
                isGeneralTournament={
                  isOpenChartModal === 1
                    ? Boolean(selectedTab.f11)
                    : Boolean(selectedTab.f5)
                }
                selectedWinner={selectedWinner}
              />
            );
          }}
          size="sm"
          title={isOpenChartModal === 1 ? 'Primera División' : 'Fútbol 5'}
          titleClassName="text-xl mb-4"
          variant="controlled"
        />
        <Modal
          className="pt-7 px-10 bg-gdt-gray-7"
          controlledOnClose={() => setIsNoRankingsModalOpen(false)}
          isControlledOpen={isNoRankingsModalOpen}
          render={() => {
            return (
              <div className="text-center py-8">
                <p className="text-lg text-gdt-gray-2">{noRankingText}</p>
                <div className="mt-8">
                  <Button
                    className="px-8 py-2"
                    type="button"
                    variant="primary"
                    onClick={() => setIsNoRankingsModalOpen(false)}
                  >
                    Aceptar
                  </Button>
                </div>
              </div>
            );
          }}
          size="xs"
          title="Rankings"
          titleClassName="text-xl"
          variant="controlled"
        />
      </Container>
    </NavBar>
  );
};

export default RankingPage;
